/**
 * Vücut kompozisyonu — saf hesaplama. Ayrıntılı gerekçe ve kaynaklar: MOTOR.md
 *
 * Buradaki her fonksiyonun bir hata payı var ve o hata payı çıktının parçası.
 * Tek bir sayı döndürüp "işte yağ oranın" demek, bu ölçümlerde yalan söylemektir.
 */

export type Sex = 'male' | 'female'

export interface Measurement {
  date: string                 // 'YYYY-MM-DD'
  waistCm: number
  neckCm?: number | null       // opsiyonel, saklanıyor ama trend olarak gösterilmiyor
  hipCm?: number | null
  note?: string | null
}

// ── VKİ ─────────────────────────────────────────────────────────────────────

export function bmi(weightKg: number, heightCm: number): number {
  const h = heightCm / 100
  return h > 0 ? weightKg / (h * h) : 0
}

export type BmiCategory =
  | 'underweight' | 'normal' | 'overweight' | 'obese1' | 'obese2' | 'obese3'

export const BMI_LABEL: Record<BmiCategory, string> = {
  underweight: 'Zayıf',
  normal: 'Normal',
  overweight: 'Fazla kilolu',
  obese1: 'Obezite — sınıf I',
  obese2: 'Obezite — sınıf II',
  obese3: 'Obezite — sınıf III',
}

export function bmiCategory(value: number): BmiCategory {
  if (value < 18.5) return 'underweight'
  if (value < 25) return 'normal'
  if (value < 30) return 'overweight'
  if (value < 35) return 'obese1'
  if (value < 40) return 'obese2'
  return 'obese3'
}

/** Verilen VKİ'ye denk gelen kilo — hedef çizgileri için. */
export function weightAtBmi(target: number, heightCm: number): number {
  const h = heightCm / 100
  return target * h * h
}

// ── Bel ve bel/boy oranı ────────────────────────────────────────────────────

/** WHO/NHLBI erkek eşikleri. Kadında 80 / 88. */
export function waistRisk(waistCm: number, sex: Sex): 'low' | 'increased' | 'high' {
  const [a, b] = sex === 'male' ? [94, 102] : [80, 88]
  if (waistCm < a) return 'low'
  if (waistCm < b) return 'increased'
  return 'high'
}

export function whtr(waistCm: number, heightCm: number): number {
  return heightCm > 0 ? waistCm / heightCm : 0
}

/** NICE NG246 (2026) bantları. */
export function whtrBand(value: number): 'healthy' | 'increased' | 'high' {
  if (value < 0.5) return 'healthy'
  if (value < 0.6) return 'increased'
  return 'high'
}

export const WHTR_LABEL = {
  healthy: 'Sağlıklı aralık',
  increased: 'Artmış merkezi yağlanma riski',
  high: 'Yüksek risk',
} as const

/** "Belini boyunun yarısının altında tut" — WHtR 0.5'e denk gelen bel. */
export function waistTarget(heightCm: number): number {
  return heightCm * 0.5
}

// ── Yağ oranı ───────────────────────────────────────────────────────────────

/**
 * Relative Fat Mass (Woolcott & Bergman 2018). Sadece bel + boy istiyor.
 *
 * US Navy formülü yerine bu seçildi: Navy boyun ölçümü istiyor ve boyun,
 * kendi kendine ölçümde en güvenilmez çevre (ICC 0.84, uyum sınırı ±5 cm) —
 * yani 12 haftalık gerçek değişimden büyük bir gürültü kaynağı. Ayrıca Navy
 * obez bireylerde DXA'ya karşı 5–6 puan düşük tahmin ediyor.
 */
export function rfm(waistCm: number, heightCm: number, sex: Sex): number {
  if (waistCm <= 0 || heightCm <= 0) return 0
  return (sex === 'male' ? 64 : 76) - 20 * (heightCm / waistCm)
}

/**
 * Tahminin belirsizliği. Sınıf II/III obezitede RFM'in DXA'ya karşı uyum
 * sınırı −7.7% … +8.3%; daha zayıf bireylerde RFM'in doğruluğu düşüyor
 * ama mutlak sapma daralıyor.
 */
export const RFM_UNCERTAINTY = 8

export function rfmRange(value: number): { low: number; high: number } {
  return {
    low: Math.max(0, value - RFM_UNCERTAINTY),
    high: Math.min(100, value + RFM_UNCERTAINTY),
  }
}

/** ACE/ACSM sınıflandırması (erkek). Kadında sınırlar 8 puan yukarı kayıyor. */
export function bodyFatBand(
  value: number, sex: Sex,
): 'athletic' | 'fitness' | 'average' | 'obese' {
  const shift = sex === 'male' ? 0 : 8
  if (value < 14 + shift) return 'athletic'
  if (value < 18 + shift) return 'fitness'
  if (value < 25 + shift) return 'average'
  return 'obese'
}

export const BODY_FAT_LABEL = {
  athletic: 'Atletik',
  fitness: 'Fit',
  average: 'Ortalama',
  obese: 'Yüksek',
} as const

export function fatMassKg(weightKg: number, bodyFatPct: number): number {
  return weightKg * (bodyFatPct / 100)
}

export function leanMassKg(weightKg: number, bodyFatPct: number): number {
  return weightKg - fatMassKg(weightKg, bodyFatPct)
}

/**
 * Hedef kiloya ulaşıldığında yağ oranı ne olur?
 *
 * `leanShare` = kaybedilen kütlenin yağsız dokudan gelen payı. Direnç
 * antrenmanı + yeterli protein bunu 0.10–0.20'ye çekiyor; hiç antrenman
 * yoksa klasik 0.25 kuralı geçerli. Bu fark, aynı hedef kiloda birkaç puanlık
 * yağ oranı demek — antrenmanın asıl gerekçesi bu, "daha hızlı kilo vermek" değil.
 */
export function projectBodyFat(
  weightKg: number, bodyFatPct: number, targetWeightKg: number, leanShare: number,
): number {
  const lost = weightKg - targetWeightKg
  if (lost <= 0 || targetWeightKg <= 0) return bodyFatPct

  const fat = fatMassKg(weightKg, bodyFatPct) - lost * (1 - leanShare)
  return Math.max(0, (fat / targetWeightKg) * 100)
}

/** Antrenmanın aynı kiloda kazandırdığı yağ oranı farkı (puan). */
export function trainingBenefit(
  weightKg: number, bodyFatPct: number, targetWeightKg: number,
): number {
  const withTraining = projectBodyFat(weightKg, bodyFatPct, targetWeightKg, 0.15)
  const without = projectBodyFat(weightKg, bodyFatPct, targetWeightKg, 0.25)
  return without - withTraining
}

// ── Ölçüm gürültüsü ─────────────────────────────────────────────────────────

/**
 * Bel ölçümünde bu eşiğin altındaki değişim "değişim" sayılmaz.
 * 3 tekrarın ortalamasıyla pratik ölçüm hatası ±1.0–1.5 cm; 3 cm bunun
 * rahatça üstünde ve 12 haftalık beklenen değişimin (5–10 cm) altında.
 */
export const WAIST_NOISE_CM = 3

export function waistChangeIsReal(deltaCm: number): boolean {
  return Math.abs(deltaCm) >= WAIST_NOISE_CM
}

/** Yaşı doğum tarihinden hesapla — girilen yaş bayatlıyor, doğum tarihi bayatlamıyor. */
export function ageFrom(birthDate: string, todayIso: string): number {
  const [by, bm, bd] = birthDate.split('-').map(Number)
  const [ty, tm, td] = todayIso.split('-').map(Number)
  if (!by || !ty) return 0
  let age = ty - by
  if (tm < bm || (tm === bm && td < bd)) age -= 1
  return Math.max(0, age)
}
